import { makeAutoObservable } from 'mobx'
import {
  getComments,
  getImages,
  getOrderHistory,
} from '../../../services/ApiService'
import { getNormalizedHistoryData } from '../helpers/normalizeHistoryData'
import { getPreparedImages } from '../helpers/setImageUrl'
import { RequestType } from '../types/Request.type'
import {
  CommentType,
  HistoryItemType,
  ImageResponseType,
  ImagesKeys,
  LargeImageItemType,
} from '../types/store.types'

export type CollectionItem = {
  key: string
  interval: [number, number]
  length: number
}

type UpdateCallbackType = (req: RequestType[]) => void

class SingleRequestStore {
  request: RequestType | null
  requestIndex: number | null
  maxIndex: number | null
  images: LargeImageItemType[] = []
  imagesCollections: CollectionItem[] = []
  isImagesLoading: boolean = false
  comments: CommentType[]
  history: HistoryItemType[]
  isHistoryLoading: boolean = false
  firstImageCollectionKey: ImagesKeys
  storeUpdateCallback: null | UpdateCallbackType = null

  constructor() {
    this.request = null
    this.requestIndex = null
    this.maxIndex = null
    this.comments = []
    this.history = []

    this.firstImageCollectionKey = 'installImages'

    makeAutoObservable(this)
  }

  setNewHistory = (newHistory: HistoryItemType[]) => {
    this.history = newHistory
  }

  setNewComments = (newComments: CommentType[]) => {
    this.comments = newComments
  }

  setUpdateCallback = (callback: UpdateCallbackType) => {
    this.storeUpdateCallback = callback
  }

  get currentRequest() {
    return this.request
  }

  get currentRequestIndex() {
    return this.requestIndex
  }

  setRequest = async (request: RequestType, index: number) => {
    this.isImagesLoading = true
    this.isHistoryLoading = true
    await this.setImages(request.id)
    this.isImagesLoading = false
    this.request = request
    await this.getComments(request.id)
    await this.getHistory()
    this.isHistoryLoading = false
    this.requestIndex = index
  }

  resetRequest = () => {
    this.request = null
    this.resetImagesCollections()
    this.history = []
    this.comments = []
    this.requestIndex = 0
  }

  setImages = async (requestId: number) => {
    const images: ImageResponseType = await getImages(requestId).catch(
      this.imageErrorCatch,
    )

    this.resetImagesCollections()

    getPreparedImages(images).forEach((imagesCollection) => {
      let interval: [number, number] = [this.images.length, 0]
      this.images = [...this.images, ...imagesCollection.images]
      interval = [interval[0], this.images.length - 1]

      const length =
        interval[0] === 0 ? interval[1] + 1 : interval[1] - interval[0] + 1

      this.imagesCollections.push({
        key: imagesCollection.key,
        interval,
        length,
      })
    })

    this.firstImageCollectionKey = this.getFirstNotEmptyKey()
  }

  resetImagesCollections = () => {
    this.images = []
    this.imagesCollections = []
  }

  getFirstNotEmptyKey = (): ImagesKeys => {
    return (this.imagesCollections[0]?.key ?? 'installImages') as ImagesKeys
  }

  getComments = async (requestId: number) => {
    await getComments(requestId)
      .then((res) => {
        this.comments = res.reverse()
      })
      .catch(() => {
        this.comments = []
      })
  }

  getHistory = async () => {
    if (!this.request) {
      return
    }
    this.history = []
    const history = await getOrderHistory(this.request.id)
    this.history = getNormalizedHistoryData(history, (_id: number) => '-')
  }

  updateHistory = () => {
    this.getHistory()
  }

  updateRequest = (request: RequestType) => {
    this.request = request
  }

  setMaxIndex = (maxIndex: number) => {
    this.maxIndex = maxIndex
  }

  imageErrorCatch = () => {
    this.resetImagesCollections()
    return {}
  }
}

const singleRequestStore = new SingleRequestStore()

export default singleRequestStore
